//Built-in libraries

//Third party libraries
import { ArchiveBoxIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { patchArchiveProject } from '@/api/Sherpa/Project';
import type { IGetProjectsServiceResData } from '@/api/Sherpa/Project/modules/ProjectCore/types';
//Custom components
import Table from '@/components/Table/Table';
import Button from '@/components/UI/Button';
import Confirm from '@/components/UI/Modal/Confirm';
import { useProjectsData } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { workspaceSelector } from '@/store';
import { notifyUser } from '@/store/Toast';
//Assets and Miscellaneous
import type { FiltersInterface } from '@/types/Miscellaneous';
//Services
import { isObjectEmpty, sanitizeResponseError } from '@/utils';
import { useResetProjectListPage } from '../useResetProjectListPage';
import ProjectRow from './ProjectRow';

//Component Begin
export const TableViewProjectsList = ({ filters }: { filters: FiltersInterface }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [perPage, setperPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const { currentWorkspace } = useSelector(workspaceSelector);
  const [selectedProjects, setSelectedProjects] = useState<number[]>([]);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isBulkSelected, setIsBulkSelected] = useState(false);

  const fetchProjects = useProjectsData({
    params: { perPage, page, ...filters },
  });
  const projects = fetchProjects?.data?.data;
  const totalProjects = fetchProjects?.data?.meta?.total;
  const totalPages = Math.ceil(Number(totalProjects) / perPage);
  useResetProjectListPage(filters, setPage);

  const onPageChangeHandler = (direction: string) => {
    if (direction === 'prev') {
      setPage((prevState) => (prevState > 1 ? prevState - 1 : 1));
    } else if (direction === 'next') {
      setPage((prevState) => (prevState >= totalPages ? totalPages : prevState + 1));
    }
  };

  const archiveProject = useMutation(
    () => {
      return Promise.all(selectedProjects.map((spid: number) => patchArchiveProject({ projectId: spid })));
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['get-projects']);
        setShowArchiveConfirm(false);
        setSelectedProjects([]);
        setIsBulkSelected(false);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Bulk Archive successful',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const confirmArchiveProject = async () => {
    archiveProject.mutate();
  };

  const cancelArchiveProject = () => {
    setShowArchiveConfirm(false);
  };

  return (
    <>
      {showArchiveConfirm && (
        <Confirm
          title='Archive Project'
          message='You are about to archive selected project(s). Are you sure you want to archive ?'
          acceptButtonLabel='Archive'
          declineButtonLabel='Cancel'
          accept={confirmArchiveProject}
          decline={cancelArchiveProject}
        />
      )}

      {projects && projects?.length > 0 && (
        <>
          {selectedProjects.length > 0 && (
            <Button
              label='Archive'
              customClassName='border-2'
              btnStyle='neutral'
              btnSize='md'
              icon={<ArchiveBoxIcon className='h-5 w-5' />}
              onClick={() => setShowArchiveConfirm(true)}
            />
          )}
          <p className='my-5'>{totalProjects} Projects</p>
          <Table
            tableHead={['Project Name', 'Status', 'Tasks', 'Deliverables', 'Milestones', 'Project Lead']}
            currentPage={page}
            totalPage={totalPages}
            perPageHandler={(v) => {
              setperPage(+v.target.value);
              setPage(1);
            }}
            pageChangeHandler={onPageChangeHandler}
            isBulkSelected={isBulkSelected}
            handleBulkSelected={(s) => {
              if (s) {
                setSelectedProjects(projects.map((p: any) => p.id));
              } else {
                setSelectedProjects([]);
              }
              setIsBulkSelected(s);
            }}
          >
            {fetchProjects.isLoading &&
              [1, 2, 3, 4, 5].map((num) => (
                <tr key={num} className='animate-pulse'>
                  <td className='px-6 py-4 whitespace-nowrap w-full'>
                    <div className='flex items-center'>
                      <span className='inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-500'>
                        <span className='font-medium leading-none text-green-500 font-bold'></span>
                      </span>
                      <div className='ml-4 h-2 w-1/3'>
                        <div className='h-1 w-full rounded-md bg-gray-500 mb-2'></div>
                        <div className='h-1 w-1/2 rounded-md bg-gray-500'></div>
                      </div>
                    </div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap w-full'>
                    <div className='h-1 w-1/3 rounded-md bg-gray-500 mb-2'></div>
                    <div className='h-1 w-1/2 rounded-md bg-gray-500'></div>
                  </td>
                  <td className='whitespace-nowrap w-full text-center'>
                    <span className='h-4 w-1/2 inline-flex rounded-full bg-gray-500'></span>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap w-full'>
                    <div className='h-2 rounded-md bg-gray-500 mb-2'></div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                    <span className='block h-1 w-2 rounded-md bg-gray-500 mb-2'></span>
                  </td>
                </tr>
              ))}
            {!fetchProjects.isLoading &&
              projects.map((project: IGetProjectsServiceResData) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  isLoading={fetchProjects.isLoading}
                  selectedProjects={selectedProjects}
                  handleProjectSelected={(pid, checked) => {
                    if (checked) {
                      setSelectedProjects((prevState) => {
                        return [...prevState, pid];
                      });
                    } else {
                      const currentSelectedProjects = [...selectedProjects];
                      const updatedSelectedProjects = currentSelectedProjects.filter((p) => p !== pid);
                      setSelectedProjects(updatedSelectedProjects);
                    }
                  }}
                />
              ))}
          </Table>
        </>
      )}
      {!fetchProjects.isLoading && !isObjectEmpty(filters) && totalProjects === 0 && (
        <section className='w-full flex justify-center items-center text-center h-screen/2 bg-white p-4'>
          <div>
            <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
              <MapPinIcon className='h-8 w-8' />
              <h1>{totalProjects} Projects</h1>
            </div>
            <p>
              There are {totalProjects} projects matching your criteria in{' '}
              {currentWorkspace ? <span className='text-primaryColor-normal'>{currentWorkspace?.name}</span> : 'all'} program.
            </p>
          </div>
        </section>
      )}
    </>
  );
};
